<template>
    <div class="relative">
        <div class="mb-8 grid gap-4 grid-cols-12">
            <Btn
                v-if="!value.published_at"
                class="col-start-10 col-span-1"
                type="button"
                :disabled="!value.id"
                content="Set as Active"
                @click="publishMenu"
            ></Btn>
            <Btn
                type="button"
                :disabled="!value.items.length"
                class="col-start-11 col-span-1"
                :content="value.id ? 'Update Menu' : 'Create Menu'"
                @click="saveMenu"
            ></Btn>
        </div>
        <div class="flex mb-6 w-full overflow-x-scroll">
            <draggable
                v-model="menu.items"
                class="flex flex-row"
                @choose="froalaStore.disableFroala = true"
                @unchoose="froalaStore.disableFroala = false"
            >
                <div
                    v-for="(item, index) in menu.items"
                    :key="`menu-item-${index}`"
                    class="bg-white rounded-lg shadow mr-4 p-4 cursor-pointer relative"
                    style="min-width: 240px"
                >
                    <menu-item
                        :item="item"
                        :index="index"
                        @remove-item="removeItem(index)"
                        @add-document="addDocument(index)"
                        @add-parent="addParent(index)"
                        :is-main-menu="true"
                        @remove-sub-item="removeSubItem"
                        @change-parent="replaceParent(index)"
                        @remove-parent="removeParent(index)"
                        :error="
                            errors[`items.${index}.name`]
                                ? errors[`items.${index}.name`][0]
                                : null
                        "
                    ></menu-item>
                </div>
            </draggable>
            <div
                class="flex flex-col rounded-lg border-2 border-gray-400 border-dashed p-6 items-center opacity-50 hover:opacity-100 cursor-pointer justify-center text-gray-400"
                @click="addMenuItem"
                style="min-width: 240px"
            >
                <svg
                    class="w-8 h-8 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                </svg>
                <p class="font-semibold">Click to add a new item</p>
            </div>
        </div>
        <slide-over
            :language="language"
            :is-open="slideOver.isOpen"
            @close="slideOver.isOpen = false"
            component="ModelList"
            :model="documentType"
            :excluded-documents="selectedItems"
            :list-route="contentTypes[documentType].listRoute"
            :label="contentTypes[documentType].columnSelector"
            :parent="
                filterParent
                    ? {
                          id: currentParent ? currentParent.id : null,
                          type: currentParent
                              ? currentParent.document_type ||
                                currentParent.model_name
                              : null,
                      }
                    : null
            "
            :query="{
                status_id: 1,
            }"
            @created="documentAdded"
            @selected="documentAdded"
        ></slide-over>
        <div
            class="border bg-white rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
        >
            <span v-if="!value.id"
                ><p>Please save the menu first to assign Top Choices</p></span
            >
            <p v-if="!!value.id" class="text-gray-700 mb-4">
                Top Choices (Max. 8)
            </p>
            <multi-reference
                create-text="Create new top choice link and link top choice to this page"
                link-text="Link existing top choices"
                v-if="!!value.id"
                :linkable="false"
                :language="language"
                :force-delete="true"
                v-model="menu.top_choices"
                field-name="top_choices"
                :max-items="8"
                model-data="Menu Top Choice"
                :pre-filled="{
                    menu: menu || null,
                    menu_id: menu.id || null,
                }"
            />
        </div>
    </div>
</template>

<script>
import draggable from "vuedraggable";
import EventBus from "../../eventBus";
import reference from "../../mixins/reference";
import menuBuilder from "../../mixins/menu-builder";
import siteLanguage from '../../config/siteLanguage'
import {useFroalaStore} from "../../stores/froala";

export default {
    name: "MenuBuilder",
    components: { draggable },
    mixins: [reference, menuBuilder],
    props: {
        value: {
            type: Object,
            required: false,
        },
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH, id: siteLanguage.language.ENGLISH };
            },
        },
    },
    data() {
        return {
            menu: this.value || {
                items: [],
                top_choices: [],
            },
            formError: false,
            slideOver: {
                isOpen: false,
            },
            currentMenuItem: null,
            documentType: "Menu Document",
            isParent: false,
            froalaStore: useFroalaStore(),
        };
    },
    methods: {
        replaceParent(index) {
            this.isParent = true;
            this.addParent(index);
        },
        removeParent(index) {
            this.menu.items[index].document = null;
        },
        documentAdded(object) {
            if (this.isParent) {
                this.addTopLevelParent(object);
            } else {
                this.addDocumentAsItem(object);
            }
            this.isParent = false;
        },
        addTopLevelParent({ object: document }) {
            this.menu.items[this.currentMenuItem].document = document;
            this.menu.items[this.currentMenuItem].name =
                document[this.contentTypes[this.documentType].columnSelector];
            this.slideOver.isOpen = false;
        },
        addMenuItem: function () {
            this.menu.items.push({
                name: "",
                document: null,
                children: [],
            });
            this.$nextTick(() => {
                EventBus.$emit("nameInputFocus", this.menu.items.length - 1);
            });
        },
        addDocumentAsItem: function ({ object: document }) {
            this.menu.items[this.currentMenuItem].children.push(document);
            this.slideOver.isOpen = false;
        },
        addDocument: function (menuItemIndex) {
            this.isParent = false;
            this.documentType = "Menu Document";
            this.currentMenuItem = menuItemIndex;
            this.slideOver.isOpen = true;
        },
        addParent: function (menuItemIndex) {
            this.isParent = true;
            this.documentType = "Menu Document";
            this.currentMenuItem = menuItemIndex;
            this.slideOver.isOpen = true;
        },
        removeItem: function (index) {
            const canDelete = confirm(
                "Are you sure you want to remove this menu item?"
            );
            if (canDelete) {
                this.menu.items.splice(index, 1);
                this.currentMenuItem = null;
            }
        },
        removeSubItem: function ({ index, subIndex }) {
            const canDelete = confirm(
                "Are you sure you want to remove this sub menu item?"
            );
            if (canDelete) {
                this.menu.items[index].children.splice(subIndex, 1);
            }
        },
        saveMenu: function () {
            const formRoute = this.isEdit
                ? route("api.menus.update", this.menu)
                : route("api.menus.store", this.language.iso);
            axios
                .post(formRoute, this.menu)
                .then((response) => {
                    this.formError = false;
                    if (response.data.redirectRoute) {
                        this.redirectRaw(response.data.redirectRoute);
                    } else {
                        this.redirectRaw(route("menus.old-index"));
                    }
                })
                .catch((error) => {
                    this.handleErrors(error);
                });
        },
    },
    computed: {
        currentItem() {
            return this.menu.items[this.currentMenuItem];
        },
        currentParent() {
            if (this.currentMenuItem !== null) {
                return this.currentItem.document || null;
            } else {
                return null;
            }
        },
        filterParent() {
            //TEMPORARY DISABLING SO THE MENU CAN BE BUILT
            return false;
            // return this.documentType === "Menu Document" && !this.isParent;
        },
        selectedItems() {
            if (!this.menu.items) return [];
            return this.menu.items
                .map((item) => item.document || null)
                .filter((item) => !!item)
                .map((document) => {
                    return {
                        id: document.id,
                        document_type:
                            document.document_type || document.model_name,
                    };
                });
        },
    },
    watch: {
        menu: {
            deep: true,
            handler: function () {
                this.$emit("input", this.menu);
            },
        },
    },
};
</script>
